import { CircularProgress, makeStyles } from '@material-ui/core';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import MessagesContext from '../../../contexts/messagesContext';
import LanguageContext from '../../../contexts/languageContext';
import { useDispatch, useSelector } from 'react-redux';
import * as type from '../../../redux/Search/SearchType';
import { createLoadingSelector } from '../../../redux/Loading/LoadingSelector';
import notExistPatient from '../../../assets/images/notExistPatients.svg';
import ResultHeader from '../Result/resultHeader';
import { PropTypes } from 'prop-types';
import ResultTable from './ResultTable';

const styles = makeStyles({
    container: {
        background: '#fff',
        marginTop: '25px',
        marginBottom: '-40px',
        padding: '24px 40px 24px 40px',
        borderRadius: '8px',
        minHeight: 'calc(100vh - 170px)'
    },
    headerContainer: {
        background: '#fff',
        position: 'sticky',
        top: '-60px'
        // paddingTop: '24px'
    },
    tableContainer: {
        overflowX: 'auto'
    },
    table: ({ rtl }) => ({
        textAlign: rtl ? 'right' : 'left',
        borderCollapse: 'collapse',
        width: '100%',
        '& td,th': {
            textAlign: rtl ? 'right' : 'left',
            padding: '15px',
            minWidth: '200px'
        }
    }),
    header: {
        borderBottom: 'solid 1.5px rgba(206, 210, 214, 0.8)'
    },
    row: {
        borderRadius: '4px',
        '&:nth-child(odd)': {
            background: '#e8ecf2'
        },
        '& td:first-child': {
            borderRadius: ({ rtl }) => (rtl ? '0 4px 4px 0' : '4px 0 0 4px')
        },
        '& td:last-child': {
            borderRadius: ({ rtl }) => (rtl ? '4px 0 0 4px' : '0 4px 4px 0')
        }
    },
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'calc(100vh - 300px)'
    },
    notExistContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: 'calc(100vh - 200px)',
        '& span': {
            fontSize: '14px',
            fontWeight: 'normal'
        }
    }
});
const convertIdToValue = (selectValue, key, id) => {
    const value = selectValue?.find((item) => item?.name === key && item.id === id)?.title;
    return value;
};
const isSelectField = (selectValue, key) => {
    return selectValue?.some((val) => val.name === key);
};
const Result = ({ setDownloadDialog }) => {
    const { lang } = useContext(LanguageContext);
    const classes = styles({ rtl: lang === 'fa' });
    const { messages } = useContext(MessagesContext);
    const fields = useSelector((state) => state.search.fields);
    const selectValue = useSelector((state) => state.search.selectValue);
    const list = useSelector((state) => state.search.searchResult);
    const isSearched = useSelector((state) => state.search.isSearched);
    const loadingSelector = createLoadingSelector(type.SEARCH_FORM_REQUEST);
    const isLoading = useSelector(loadingSelector);
    const selectedFields = useRef();
    const dispatch = useDispatch();

    // reproduce data of table
    let data = [];
    list?.forEach((item) =>
        item.indicator_fields?.length
            ? item.indicator_fields.forEach((indicator) => {
                  const keys = Object.keys(indicator)?.filter((item) => item !== 'structure');
                  keys.forEach((key) => {
                      const id = indicator[key]?.toString();
                      const findExist = data.find((d) => d.id === item.id);
                      const isSelect = isSelectField(selectValue, key);
                      const name = isSelect ? key + id : key;
                      if (findExist?.id) {
                          data = data.map((val) =>
                              val.id === findExist.id
                                  ? {
                                        ...val,
                                        [name]: convertIdToValue(selectValue, key, id) ?? id
                                    }
                                  : val
                          );
                      } else {
                          data = [
                              ...data,
                              {
                                  [name]: convertIdToValue(selectValue, key, id) ?? id,
                                  firstName: item.first_name + ' ' + item.last_name,
                                  id: item.id
                              }
                          ];
                      }
                  });
              })
            : (data = [...data, { firstName: item.first_name + ' ' + item.last_name, id: item.id }])
    );

    // const uniqueData = useMemo(() => {
    //     return data?.reduce((acc, cur) => {
    //         const item = acc.length ? acc.find(({ id }) => id === cur.id) : {};
    //         if (item?.id) {
    //             return [{ ...item, ...cur }];
    //         } else acc.push(cur);
    //         return acc;
    //     }, []);
    // }, [data]);

    const nameAndLastName = { Header: messages.nameAndLastName, accessor: 'firstName' };

    const columnsData =
        selectedFields?.current?.map((item) => {
            const isSelect = isSelectField(selectValue, item.field.id);
            const name = isSelect ? item.field.id + item.value : item.field.id;
            return {
                Header: item.field.name,
                accessor: name
            };
        }) ?? [];

    const columns = useMemo(() => {
        return [nameAndLastName, ...columnsData];
    }, [nameAndLastName, columnsData]);

    useEffect(() => {
        if (isSearched) {
            selectedFields.current = fields?.filter((item) => item.structure !== 'patient');
            dispatch({ type: type.IS_SEARCHED });
        }
    }, [fields, isSearched]);
    const keys = new Set();
    for (const fieldWrapper of fields) {
        if (fieldWrapper.structure !== 'patient' && 'id' in fieldWrapper.field) {
            keys.add(fieldWrapper.field.id);
        }
    }
    return (
        <div className={classes.container}>
            {list?.length ? (
                <ResultHeader keys={Array.from(keys)} setDownloadDialog={setDownloadDialog} />
            ) : null}
            {isLoading ? (
                <div className={classes.loadingContainer}>
                    <CircularProgress />
                </div>
            ) : !list?.length ? (
                <div className={classes.notExistContainer}>
                    <img src={notExistPatient} alt="notExist" />
                    <span>{messages.noResultFound}</span>
                </div>
            ) : (
                <div className={classes.headerContainer}>
                    <div className={classes.tableContainer}>
                        {list?.length && <ResultTable columns={columns} data={data} />}
                    </div>
                </div>
            )}
        </div>
    );
};
Result.propTypes = {
    setDownloadDialog: PropTypes.func
};
export default Result;
